/**
 * Client-side wishlist stored in localStorage.
 * Keys: product handles. Works without a Shopify wishlist app.
 */
const SC_WISHLIST_KEY = 'sc-wishlist';
const SC_WISHLIST_EVENT = 'sc:wishlist:change';

const ScWishlist = {
  get() {
    try {
      const raw = JSON.parse(localStorage.getItem(SC_WISHLIST_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  },

  set(handles) {
    localStorage.setItem(SC_WISHLIST_KEY, JSON.stringify(handles));
    window.dispatchEvent(new CustomEvent(SC_WISHLIST_EVENT, { detail: { handles } }));
  },

  has(handle) {
    return this.get().includes(handle);
  },

  toggle(handle) {
    if (!handle) return false;
    const list = this.get();
    const index = list.indexOf(handle);
    if (index >= 0) {
      list.splice(index, 1);
      this.set(list);
      return false;
    }
    list.push(handle);
    this.set(list);
    return true;
  },

  remove(handle) {
    this.set(this.get().filter((h) => h !== handle));
  },

  count() {
    return this.get().length;
  },
};

window.ScWishlist = ScWishlist;

function scFormatMoney(cents) {
  const amount = (Number(cents) / 100).toFixed(2);
  const currency = window.Shopify?.currency?.active || 'CHF';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
      style: 'currency',
      currency,
    }).format(Number(amount));
  } catch (e) {
    return `${currency} ${amount}`;
  }
}

function scInitWishlistButtons() {
  document.querySelectorAll('.sc-product-wishlist[data-sc-product-handle]').forEach((el) => {
    if (el.dataset.scWishlistBound) return;
    el.dataset.scWishlistBound = '1';

    const handle = el.dataset.scProductHandle;
    const sync = () => {
      const active = ScWishlist.has(handle);
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    };

    sync();
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    if (!el.getAttribute('aria-label')) {
      el.setAttribute('aria-label', 'Wishlist');
    }

    const toggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      ScWishlist.toggle(handle);
      sync();
    };

    el.addEventListener('click', toggle);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') toggle(e);
    });

    window.addEventListener(SC_WISHLIST_EVENT, sync);
  });
}

async function scRenderWishlistPage() {
  const root = document.querySelector('[data-sc-wishlist-page]');
  if (!root) return;

  const grid = root.querySelector('[data-sc-wishlist-grid]');
  const empty = root.querySelector('[data-sc-wishlist-empty]');
  const countEl = root.querySelector('[data-sc-wishlist-count]');
  const loading = root.querySelector('[data-sc-wishlist-loading]');

  const handles = ScWishlist.get();

  if (countEl) {
    countEl.textContent = String(handles.length);
  }

  if (handles.length === 0) {
    if (loading) loading.hidden = true;
    if (grid) grid.innerHTML = '';
    if (empty) empty.hidden = false;
    return;
  }

  if (empty) empty.hidden = true;
  if (loading) loading.hidden = false;

  const products = await Promise.all(
    handles.map(async (handle) => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        return null;
      }
    })
  );

  // Drop missing products from storage
  const valid = [];
  products.forEach((product, i) => {
    if (product) valid.push(handles[i]);
    else ScWishlist.remove(handles[i]);
  });

  if (loading) loading.hidden = true;

  if (valid.length === 0) {
    if (grid) grid.innerHTML = '';
    if (empty) empty.hidden = false;
    if (countEl) countEl.textContent = '0';
    return;
  }

  if (countEl) countEl.textContent = String(valid.length);

  const removeLabel = root.dataset.scRemoveLabel || 'Remove';

  grid.innerHTML = products
    .filter(Boolean)
    .map((product) => {
      const image = product.featured_image || (product.images && product.images[0]) || '';
      const price = scFormatMoney(product.price);
      return `
        <article class="sc-wishlist-card" data-sc-wishlist-handle="${product.handle}">
          <a class="sc-wishlist-card__media" href="${product.url}">
            ${image ? `<img src="${image}" alt="${product.title.replace(/"/g, '&quot;')}" width="398" height="505" loading="lazy">` : ''}
          </a>
          <div class="sc-wishlist-card__body">
            <a class="sc-wishlist-card__title" href="${product.url}">${product.title}</a>
            <div class="sc-wishlist-card__price">${price}</div>
            <button type="button" class="sc-wishlist-card__remove" data-sc-wishlist-remove="${product.handle}" aria-label="${removeLabel}">
              ${removeLabel}
            </button>
          </div>
        </article>
      `;
    })
    .join('');

  grid.querySelectorAll('[data-sc-wishlist-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const handle = btn.dataset.scWishlistRemove;
      ScWishlist.remove(handle);
      const card = grid.querySelector(`[data-sc-wishlist-handle="${handle}"]`);
      if (card) card.remove();
      if (countEl) countEl.textContent = String(ScWishlist.count());
      if (ScWishlist.count() === 0 && empty) {
        empty.hidden = false;
        grid.innerHTML = '';
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  scInitWishlistButtons();
  scRenderWishlistPage();
});
